import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, CircularProgress,
} from '@mui/material';
import {
  Hotel, MonitorHeart, ContactMail, Healing,
  Apartment as WardIcon, AccessTime, ArrowForward,
} from '@mui/icons-material';
import axios from 'axios';
import { loadOccupancyStatsdb, loadPendingPatientsdb } from '../BaseURL';

const PRIMARY = '#1A9B7F';

/**
 * ER landing page. Aggregates a few key counts from existing endpoints
 * and offers quick-jump buttons that hop into the right ER tab.
 *
 * Receives `navigate(key)` from DepartmentLayout's render({ navigate }) ctx.
 */
export default function ERHome({ navigate }) {
  const [stats,   setStats]   = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const userInfo = (() => {
    try { return JSON.parse(localStorage.getItem('userInfo') || '{}'); } catch { return {}; }
  })();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [occ, pen] = await Promise.all([
          axios.get(loadOccupancyStatsdb).catch(() => ({ data: null })),
          axios.get(loadPendingPatientsdb).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        if (occ.data?.success) setStats(occ.data.stats || null);
        if (pen.data?.success) setPending(pen.data.pendingPatients || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => ({
    pendingAdmits:    pending.filter(p => p.action === 'Admit').length,
    pendingDetains:   pending.filter(p => p.action === 'Detain').length,
    pendingDischarges:pending.filter(p => p.action === 'Discharge').length,
    totalPending:     pending.length,
  }), [pending]);

  const totalBeds     = Number(stats?.total)    || 0;
  const occupiedBeds  = Number(stats?.occupied) || 0;
  const availableBeds = totalBeds > 0 ? Math.max(0, totalBeds - occupiedBeds) : 0;

  const today = new Date();
  const dateLabel = today.toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const tiles = [
    {
      label:     'Beds Available',
      value:     loading ? '…' : `${availableBeds}/${totalBeds || '—'}`,
      sub:       `${occupiedBeds} occupied`,
      color:     '#15803D',
      bg:        'rgba(21,128,61,0.10)',
      icon:      <Hotel sx={{ fontSize: 22 }} />,
    },
    {
      label:     'Pending Admissions',
      value:     loading ? '…' : counts.pendingAdmits,
      sub:       'awaiting bed',
      color:     PRIMARY,
      bg:        'rgba(26,155,127,0.10)',
      icon:      <Healing sx={{ fontSize: 22 }} />,
    },
    {
      label:     'Pending Detentions',
      value:     loading ? '…' : counts.pendingDetains,
      sub:       '24h observation',
      color:     '#A16207',
      bg:        'rgba(217,119,6,0.10)',
      icon:      <AccessTime sx={{ fontSize: 22 }} />,
    },
    {
      label:     'Pending Discharges',
      value:     loading ? '…' : counts.pendingDischarges,
      sub:       'queued for nurse',
      color:     '#4338ca',
      bg:        'rgba(67,56,202,0.10)',
      icon:      <WardIcon sx={{ fontSize: 22 }} />,
    },
  ];

  const quickActions = [
    {
      key:   'Records',
      label: 'Records — Register / Search',
      desc:  'Add a walk-in patient or pull up an existing record',
      icon:  <ContactMail />,
      color: PRIMARY,
    },
    {
      key:   'PatientVitals',
      label: 'Take Vitals',
      desc:  'First-pass triage vitals on arrival',
      icon:  <MonitorHeart />,
      color: '#dc2626',
    },
    {
      key:   'BedManagement',
      label: 'Assign Bed',
      desc:  'Pending bed assignment & discharge queue',
      icon:  <Hotel />,
      color: '#A16207',
    },
    {
      key:   'InPatient',
      label: 'Open Observation Workspace',
      desc:  'Vitals, nurse notes, meds, ward rounds for active patients',
      icon:  <Healing />,
      color: '#15803D',
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, fontFamily: '"Inter","Segoe UI",Arial,sans-serif' }}>
      {/* Greeting header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 700, color: PRIMARY,
          letterSpacing: '1.4px', textTransform: 'uppercase', mb: 0.5,
        }}>
          Emergency · ER / A&amp;E
        </Typography>
        <Typography sx={{
          fontSize: { xs: '1.5rem', md: '1.85rem' }, fontWeight: 700,
          color: '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.2, mb: 0.5,
        }}>
          {userInfo.name ? `Hello, ${String(userInfo.name).split(' ')[0]}` : 'Welcome'}
        </Typography>
        <Typography sx={{ fontSize: '0.92rem', color: '#64748b' }}>
          {dateLabel}
        </Typography>
      </Box>

      {/* Stat tiles */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 2, mb: 3,
      }}>
        {tiles.map(t => (
          <Paper
            key={t.label}
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #e5e9ef', borderRadius: 2.5,
              bgcolor: '#fff',
              display: 'flex', alignItems: 'center', gap: 1.5,
              transition: 'box-shadow .2s ease',
              '&:hover': { boxShadow: '0 6px 18px rgba(15,23,42,0.06)' },
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 1.75,
              bgcolor: t.bg, color: t.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {t.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8',
                letterSpacing: '0.6px', textTransform: 'uppercase',
              }}>
                {t.label}
              </Typography>
              <Typography sx={{
                fontSize: '1.4rem', fontWeight: 700, color: '#0f172a',
                lineHeight: 1.1,
              }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#cbd5e1' }} /> : t.value}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{t.sub}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Quick actions */}
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8',
          letterSpacing: '0.8px', textTransform: 'uppercase', mb: 1.25,
        }}>
          Quick Actions
        </Typography>
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 1.75,
      }}>
        {quickActions.map(a => (
          <Paper
            key={a.key}
            elevation={0}
            onClick={() => navigate && navigate(a.key)}
            role="button"
            sx={{
              p: 2,
              border: '1px solid #e5e9ef', borderRadius: 2.5,
              bgcolor: '#fff',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 1.5,
              transition: 'transform .15s ease, box-shadow .2s ease, border-color .15s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 24px rgba(15,23,42,0.08)',
                borderColor: a.color,
              },
              '&:hover .qa-arrow': { transform: 'translateX(2px)', opacity: 1 },
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 1.75,
              bgcolor: `${a.color}14`, color: a.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {a.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25,
              }}>
                {a.label}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.25 }}>
                {a.desc}
              </Typography>
            </Box>
            <Box className="qa-arrow" sx={{
              color: a.color, opacity: 0.5,
              transition: 'transform .18s ease, opacity .18s ease',
              flexShrink: 0,
            }}>
              <ArrowForward sx={{ fontSize: 20 }} />
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
